import React, { useState } from 'react';
import { array, arrayOf, func, node, number, object, oneOf, string } from 'prop-types';
import classNames from 'classnames';
import { useFormState } from 'react-final-form';

import { useIntl } from '../../../util/reactIntl';
import { parseSelectFilterOptions } from '../../../util/search';
import { SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM } from '../../../util/types';

import { FieldCheckbox } from '../../../components';

import FilterPlain from '../FilterPlain/FilterPlain';
import FilterPopup from '../FilterPopup/FilterPopup';

import css from './SelectMultipleFilter.module.css';

// When option lists get long enough that scrolling hurts discoverability,
// add an inline search field above the checkbox list.
const SEARCHABLE_THRESHOLD = 10;

// Progressive disclosure: collapse long lists to the top few options with a
// "Visa fler" toggle, so e.g. 30+ hub-bore values don't overwhelm the column.
const COLLAPSE_THRESHOLD = 8;
const DEFAULT_VISIBLE_COUNT = 5;

// SelectMultipleFilter doesn't need array mutators since it doesn't require validation.
// TODO: Live edit didn't work with FieldCheckboxGroup
//       There's a mutation problem: formstate.dirty is not reliable with it.
const GroupOfFieldCheckboxes = props => {
  const { id, className, name, options, searchPlaceholder } = props;
  const [query, setQuery] = useState('');
  const [expanded, setExpanded] = useState(false);
  const searchable = options.length >= SEARCHABLE_THRESHOLD;

  // Read the currently selected values so collapsing never hides a checked option.
  const { values } = useFormState({ subscription: { values: true } });
  const selectedValues = Array.isArray(values?.[name]) ? values[name] : [];
  const isSelected = option => selectedValues.some(v => String(v) === String(option));

  const normalized = query.trim().toLowerCase();
  const filteredOptions = searchable && normalized
    ? options.filter(opt => {
        const label = typeof opt.label === 'string' ? opt.label.toLowerCase() : '';
        const value = typeof opt.option === 'string' ? opt.option.toLowerCase() : '';
        return label.includes(normalized) || value.includes(normalized);
      })
    : options;

  // Collapse only when not actively searching and the list is long.
  const collapsible = !normalized && filteredOptions.length > COLLAPSE_THRESHOLD;
  const visibleOptions =
    collapsible && !expanded
      ? filteredOptions.filter((opt, i) => i < DEFAULT_VISIBLE_COUNT || isSelected(opt.option))
      : filteredOptions;
  const hiddenCount = filteredOptions.length - visibleOptions.length;

  return (
    <fieldset className={className}>
      {searchable ? (
        <input
          type="text"
          className={css.searchInput}
          placeholder={searchPlaceholder || 'Sök…'}
          value={query}
          onChange={e => setQuery(e.target.value)}
          aria-label={searchPlaceholder || 'Sök'}
        />
      ) : null}
      <ul className={classNames(css.list, { [css.scrollableList]: searchable })}>
        {visibleOptions.map(optionConfig => {
          const { option, label } = optionConfig;
          const fieldId = `${id}.${option}`;
          return (
            <li key={fieldId} className={css.item}>
              <FieldCheckbox id={fieldId} name={name} label={label} value={option} />
            </li>
          );
        })}
        {searchable && visibleOptions.length === 0 ? (
          <li className={css.noMatches}>Inga träffar</li>
        ) : null}
      </ul>
      {collapsible ? (
        <button
          type="button"
          className={css.showMoreButton}
          onClick={() => setExpanded(prev => !prev)}
          aria-expanded={expanded}
        >
          {expanded ? 'Visa färre' : `Visa fler (${hiddenCount})`}
        </button>
      ) : null}
    </fieldset>
  );
};

const getQueryParamName = queryParamNames => {
  return Array.isArray(queryParamNames) ? queryParamNames[0] : queryParamNames;
};

// Format URI component's query param: { pub_key: 'has_all:a,b,c' }
const format = (selectedOptions, queryParamName, schemaType, searchMode) => {
  const hasOptionsSelected = selectedOptions && selectedOptions.length > 0;
  const mode = schemaType === SCHEMA_TYPE_MULTI_ENUM && searchMode ? `${searchMode}:` : '';
  const value = hasOptionsSelected ? `${mode}${selectedOptions.join(',')}` : null;
  return { [queryParamName]: value };
};

const SelectMultipleFilter = props => {
  const intl = useIntl();
  const {
    rootClassName,
    className,
    id,
    name,
    label,
    options,
    initialValues,
    contentPlacementOffset,
    onSubmit,
    queryParamNames,
    schemaType,
    searchMode,
    showAsPopup,
    ...rest
  } = props;

  const classes = classNames(rootClassName || css.root, className);

  const resolveLabelsFromSelectedOptions = (options, selectedOptions) => {
    if (!options?.length || !selectedOptions?.length) return null;

    const labels = selectedOptions
      .map(value => {
        const match = options.find(
          opt => String(opt.option) === String(value)
        );
        return match?.label;
      })
      .filter(Boolean);

    return labels.length ? labels.join(', ') : null;
  };


  const queryParamName = getQueryParamName(queryParamNames);
  const hasInitialValues = !!initialValues && !!initialValues[queryParamName];
  // Parse options from param strings like "has_all:a,b,c" or "a,b,c"
  const selectedOptions = hasInitialValues
    ? parseSelectFilterOptions(initialValues[queryParamName])
    : [];

  const selectedLabels = resolveLabelsFromSelectedOptions(
    options,
    selectedOptions
  );

  const labelForPopup = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilter.labelSelected' },
      { labelText: label, count: selectedOptions.length }
    )
    : label;

  const labelSelectionForPlain = hasInitialValues
    ? intl.formatMessage(
      { id: 'SelectMultipleFilterPlainForm.labelSelected' },
      { count: selectedLabels }
    )
    : '';

  // pass the initial values with the name key so that
  // they can be passed to the correct field
  const namedInitialValues = { [name]: selectedOptions };

  const handleSubmit = values => {
    const usedValue = values ? values[name] : values;
    onSubmit(format(usedValue, queryParamName, schemaType, searchMode));
  };

  return showAsPopup ? (
    <FilterPopup
      className={classes}
      rootClassName={rootClassName}
      popupClassName={css.popupSize}
      label={labelForPopup}
      isSelected={hasInitialValues}
      id={`${id}.popup`}
      showAsPopup
      contentPlacementOffset={contentPlacementOffset}
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      keepDirtyOnReinitialize
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroup}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        searchPlaceholder={
          typeof label === 'string' ? `Sök ${label.toLowerCase()}…` : 'Sök…'
        }
      />
    </FilterPopup>
  ) : (
    <FilterPlain
      className={className}
      rootClassName={rootClassName}
      label={label}
      labelSelection={labelSelectionForPlain}
      isSelected={hasInitialValues}
      id={`${id}.plain`}
      liveEdit
      onSubmit={handleSubmit}
      initialValues={namedInitialValues}
      {...rest}
    >
      <GroupOfFieldCheckboxes
        className={css.fieldGroupPlain}
        name={name}
        id={`${id}-checkbox-group`}
        options={options}
        searchPlaceholder={
          typeof label === 'string' ? `Sök ${label.toLowerCase()}…` : 'Sök…'
        }
      />
    </FilterPlain>
  );
};

SelectMultipleFilter.defaultProps = {
  rootClassName: null,
  className: null,
  initialValues: null,
  contentPlacementOffset: 0,
  searchMode: null,
};

SelectMultipleFilter.propTypes = {
  rootClassName: string,
  className: string,
  id: string.isRequired,
  name: string.isRequired,
  queryParamNames: arrayOf(string).isRequired,
  label: node.isRequired,
  onSubmit: func.isRequired,
  options: array.isRequired,
  searchMode: oneOf(['has_all', 'has_any']),
  schemaType: oneOf([SCHEMA_TYPE_ENUM, SCHEMA_TYPE_MULTI_ENUM]).isRequired,
  initialValues: object,
  contentPlacementOffset: number,
};

export default SelectMultipleFilter;
